import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

// Material UI
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

// Data
import { useCompanySportTerminology } from "../../../lib/stores/company.store";
import { CommunicationContextType, Sport, SportTerminology } from "../../../lib/types";
import { usePublishCommunicationContext } from '../../../lib/stores/communication.store';

// Components
import DashboardLayout from '../_layout/DashboardLayout/dashboard.layout';
import { PageTitle, SectionHeading } from '../../_core/_ui/typography.component';
import { SectionWrapper, Spacer } from '../../_core/_ui/structure.components';
import { EditPlayerGraphQL } from '../../Player/EditPlayer/edit-player.graphql';
import { PlayerMilestonesGraphQL } from '../../Player/PlayerMilestoneHistory/player-milestones.graphql';
import { ManagePlayerContactsGraphQL } from '../../PlayerContact/ManagePlayerContacts/manage-player-contacts.graphql';
import PlayerAssessmentSessionListGraphQL
  from '../../Player/PlayerAssessmentSessionList/player-assessment-session-list.component';
import { SecondaryButton } from '../../_core/_ui/buttons.component';
import EditPlayerAssessmentGraphQL from '../../Player/EditPlayerAssessment/edit-player-assessment.graphql';
import { PlayerAttendanceGraphQL } from '../../Player/PlayerAttendance/player-attendance.graphql';
import { PlayerScoresGraphQL } from '../../Player/PlayerScores/player-scores.graphql';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { SPORT_TERMINOLOGY } from '../../../lib/constants';
import { CommunicationListSection } from '../../EmailLog/EmailLogList/email-log-list.section';
import { hasAnyRoles } from '../../../lib/services/user.service';
import { useReportCardSurface } from '../../../lib/services/report-card-access.service';
import { PlayerReportCardSection } from '../../ReportCardAccess/player-report-card-section.component';
import { ContactReachAlert } from '../../PlayerContact/contact-reach-alert.component';
import { useContactReach } from '../../../lib/services/player-contact.service';
import { isContactReachGap } from '../../../lib/helpers/contact-reach.helpers';
import { ROLES } from '../../../../_configuration';
import { CircularProgress } from '@material-ui/core';
import { BiasAdjustedScoreHistoryGraphQL } from '../../Player/BiasAdjustedScoreHistory/bias-adjusted-score-history.graphql';
import Alert from '@material-ui/lab/Alert';

interface IEditPlayerPage {
  match: any;
}

const GET_PLAYER_ASSESSMENT_AGE_GROUP = gql`
  query GetPlayerAssessmentAgeGroup($_id: ID!) {
    playerAssessment(_id: $_id) {
      _id
      ageGroup {
        _id
      }
      unappliedActionsCount
    }
  }
`;

export const EditPlayerPage: React.FC<IEditPlayerPage> = ({ match }) => {
  const history = useHistory()
  const location = useLocation()
  const back = (location.state as { back?: string })?.back ?? "/" + pluralTerm(SPORT_TERMINOLOGY[Sport.default][SportTerminology.ATHLETE].toLowerCase())
  const termLookup = useCompanySportTerminology();
  const { playerId, playerAssessmentId } = match.params;

  // Publish this player as the compose panel's page context; it opens pre-applied
  // as a locked row, and a cross-assessment id simply drops out on resolve.
  usePublishCommunicationContext(useMemo(
    () => [{ type: CommunicationContextType.PLAYER_ASSESSMENT, id: playerAssessmentId }],
    [playerAssessmentId]
  ));

  const { data, loading } = useQuery(GET_PLAYER_ASSESSMENT_AGE_GROUP, {fetchPolicy: "cache-and-network", variables: {_id: playerAssessmentId}})
  const ageGroupId: string | undefined = data?.playerAssessment?.ageGroup._id;
  const hasConfigRole = hasAnyRoles({ageGroupId, roles: [ROLES.CONFIGURATION]});
  const hasReportingRole = hasAnyRoles({ageGroupId, roles: [ROLES.REPORTS]});
  const { sectionVisible: reportCardSectionVisible } = useReportCardSurface(ageGroupId);
  const contactReach = useContactReach(playerAssessmentId);

  return (
    <DashboardLayout requiredRoles={[ROLES.CONFIGURATION, ROLES.REPORTS]}>
      <Box display="flex" mb={2}>
        <PageTitle>Edit {termLookup[SportTerminology.ATHLETE]}</PageTitle>
        <Spacer />
        <SecondaryButton
          startIcon={<ArrowBackIosIcon />}
          onClick={() => history.push(back)}
        >
          Back
        </SecondaryButton>
      </Box>
      <Grid container spacing={2}>
        {isContactReachGap(contactReach) && (
          <Grid item xs={12}>
            <ContactReachAlert contactReach={contactReach} canEditContacts={hasConfigRole} />
          </Grid>
        )}
        {!!data?.playerAssessment?.unappliedActionsCount &&
          <Grid item xs={12}>
            <Alert severity="warning" variant="filled">Found {data?.playerAssessment?.unappliedActionsCount} new scores</Alert>
          </Grid>
        }
        <Grid item xs={12}>
          <SectionWrapper>
            <EditPlayerGraphQL hasConfigRole={hasConfigRole} playerId={playerId} playerAssessmentId={playerAssessmentId} />
          </SectionWrapper>
        </Grid>
        {hasConfigRole && <>
          <Grid item xs={12}>
            <SectionWrapper>
              <SectionHeading>Measurements</SectionHeading>
              <PlayerMilestonesGraphQL playerId={playerId} playerAssessmentId={playerAssessmentId} />
            </SectionWrapper>
          </Grid>
          <Grid item xs={12}>
            <SectionWrapper>
              <EditPlayerAssessmentGraphQL
                playerAssessmentId={playerAssessmentId}
              />
            </SectionWrapper>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionWrapper>
              <SectionHeading>Contacts</SectionHeading>
              <ManagePlayerContactsGraphQL playerId={playerId} playerAssessmentId={playerAssessmentId} />
            </SectionWrapper>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommunicationListSection playerAssessmentId={playerAssessmentId} />
          </Grid>
        </>}
        {!!ageGroupId && reportCardSectionVisible && (
          <Grid item xs={12}>
            <SectionWrapper>
              <PlayerReportCardSection playerAssessmentId={playerAssessmentId} ageGroupId={ageGroupId} />
            </SectionWrapper>
          </Grid>
        )}
        {/* Outside `hasConfigRole` on purpose: every viewer of this page sees the RSVP/attendance
            history. Confirm the backend's access rules before tightening this gate. */}
        <Grid item xs={12}>
          <SectionWrapper>
            <SectionHeading>Attendance</SectionHeading>
            <PlayerAttendanceGraphQL playerAssessmentId={playerAssessmentId} />
          </SectionWrapper>
        </Grid>
        <Grid item xs={12}>
          <SectionWrapper>
            <SectionHeading>
              {pluralTerm(termLookup[SportTerminology.SESSION])}
            </SectionHeading>
            <PlayerAssessmentSessionListGraphQL
              hasConfigRole={hasConfigRole}
              playerAssessmentId={playerAssessmentId}
            />
          </SectionWrapper>
        </Grid>
        {loading ?
          <Grid item xs={12}>
            <CircularProgress />
          </Grid> :
          !!hasReportingRole ? (
            <>
              <Grid item xs={12}>
                <SectionWrapper>
                  <SectionHeading>Scores</SectionHeading>
                  <PlayerScoresGraphQL playerAssessmentId={playerAssessmentId} />
                </SectionWrapper>
              </Grid>
              <Grid item xs={12}>
                <SectionWrapper>
                  <SectionHeading>UNBIAS™ Result History</SectionHeading>
                  <BiasAdjustedScoreHistoryGraphQL playerAssessmentId={playerAssessmentId} />
                </SectionWrapper>
              </Grid>
              {/* <Grid item xs={12}>
                <SectionWrapper>
                  <SectionHeading>Evaluation History</SectionHeading>
                  <PlayerEvaluationHistoryGraphQL playerAssessmentId={playerAssessmentId} />
                </SectionWrapper>
              </Grid> */}
            </>
          ) : null
        }
      </Grid>
    </DashboardLayout>
  );
};

